import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { Lock, LockOpen } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import classNames from 'classnames';
import ms from 'ms';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import Switch from 'react-switch';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchBidderCreditCardsIfNeeded } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import { getHasValidCardOnFile } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getBidderId, getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { getUiHasLoggedInBefore } from '@/redux/modules/autoPopModal/autoPopModal.selectors';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import {
    getCatalogCurrency,
    getCatalogIsAutopay,
    getCatalogIsInstantAutopay,
    getCatalogIsLAPayments,
    getCatalogIsTimed,
    getCatalogPaymentProvider,
    getCatalogSellerId,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveActiveBidder,
    getLiveBidderOutbid,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import { getLocale } from '@/redux/modules/intl';
import { getBidderSubmittedBid, submitLiveBid } from '@/redux/modules/liveBid';
import { fetchLiveCatalogStatusIfNeeded } from '@/redux/modules/liveCatalogStatus';
import {
    CompleteAccountModalContext,
    ModalContext,
    openLoginModal,
    openRegisterForAuctionModal,
} from '@/redux/modules/modal';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import noop from '@/utils/noop';
import { getDeskSupportLink } from '@/utils/urls';

import * as styles from './BiddingConsoleButton.module.scss';

// slider/switch component consts
const handleDiameter = 36;
const sliderHeight = 30;
const sliderWidth = 64;
const sliderBoxShadow = `0 1px 5px ${theme.colors.shadow500}`;
const sliderActiveBoxShadow = `0 0 1px 10px ${theme.colors.shadow200}`;
const uncheckedIcon = (
    <LockOpen
        className="ml-xs mt-2xs inline-block"
        color="inverse"
        size="sm"
        style="fill"
    />
);
const checkedIcon = (
    <Lock
        className="ml-2xs mt-2xs inline-block"
        color="inverse"
        size="sm"
        style="fill"
    />
);

type Props = {
    biddingLocked?: boolean;
    catalogId: number;
    itemId: number;
    noLinks?: boolean;
    showToggleLockedSwitch?: boolean;
    toggleBiddingLocked?: (event: any) => void;
};

const BiddingConsoleButton = ({
    biddingLocked,
    catalogId,
    itemId,
    noLinks,
    showToggleLockedSwitch = false,
    toggleBiddingLocked = noop,
}: Props) => {
    const dispatch = useAppDispatch();

    const userLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const accountInGoodStanding = useAppSelector((state) => getUserIsInGoodStanding(state));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const showItemPageVariant = false;

    const activeBidder = useAppSelector((state) => getLiveActiveBidder(state, catalogId));
    const bidderOutbid = useAppSelector((state) => getLiveBidderOutbid(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const liveCatalogStatus = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const isAutopay = useAppSelector((state) => getCatalogIsAutopay(state, catalogId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));
    const isTimed = useAppSelector((state) => getCatalogIsTimed(state, catalogId));
    const houseId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const isLAPayments = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));
    const hasCreditCard: boolean = useAppSelector((state) => getHasValidCardOnFile(state, paymentProvider));

    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const hasLoggedInBefore = useAppSelector(getUiHasLoggedInBefore);
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const locale = useAppSelector(getLocale);
    const bidderId = useAppSelector(getBidderId);
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidder = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const readyToBid = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));
    const submitted = useAppSelector(getBidderSubmittedBid);

    const location = useLocation();

    const myBid = bidderId === leadingBidder;
    const { approved, declined, limited, pending, suspended } = catalogRegistration;
    const showCountDown = isTimed && Boolean(timeStamp) && approved;

    const [, setTime] = React.useState(0);

    React.useEffect(() => {
        // bump ts to re-render the app at least once every 5 seconds
        const interval = setInterval(() => setTime(Date.now()), 5000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    React.useEffect(() => {
        dispatch(fetchBidderCreditCardsIfNeeded());
    }, [dispatch]);

    const placeBid = () => {
        // don't only rely on the "disabled" button to prevent bidders who haven't been approved
        if (accountInGoodStanding && (approved || limited) && !biddingLocked && checkReadyToBid() && !submitted) {
            dispatch(submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId, location));
        } else {
            return false;
        }
    };

    const modalContext = React.useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: true,
                isInstantAutopay,
                itemId,
                modalContext,
            })
        );
    };

    const showLoginModal = React.useCallback(() => {
        dispatch(openLoginModal());
    }, [dispatch]);

    const checkReadyToBid = () => {
        const timeSinceLastMessage = Date.now() - liveUpdateTimestamp;

        if (timeSinceLastMessage > ms('4m') && Boolean(catalogId)) {
            dispatch(fetchLiveCatalogStatusIfNeeded(catalogId, houseId, true));
        }

        return (
            readyToBid &&
            itemId === currentItem &&
            itemId > 0 &&
            networkStatus === 'connected' &&
            liveCatalogStatus === 'live' &&
            !closed &&
            !won &&
            Boolean(currentAskPrice) &&
            catalogId > 0
        );
    };

    const auctionPaused = liveCatalogStatus === 'paused';

    // ensure that we dont enable the button unless we have the required info
    const ready = checkReadyToBid();
    const wouldExceedBidLimit = limited && currentAskPrice > bidLimitBalance;

    const formattedAsk = (
        <FormattedCurrency
            className={styles.currency}
            key="bidding-console-button-formatted-ask"
            noFraction
            sourceCurrency={currency}
            value={currentAskPrice}
        />
    );

    const bidMessage = (
        <FormattedMessage
            id="bidding_console.console.button.bid"
            values={{ amount: formattedAsk }}
        />
    );

    const outbidMessage = (
        <FormattedMessage
            id="bidding_console.console.button.outbid"
            values={{ amount: formattedAsk }}
        />
    );

    let button;

    if (!userLoggedIn) {
        const loginTextId = hasLoggedInBefore
            ? 'bidding_console.console.button.login'
            : 'bidding_console.console.button.signup';

        const classes = classNames(styles.tertiary, styles['action-button']);
        button = (
            <Button
                className={classes}
                locale={locale}
                onClick={showLoginModal}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
            >
                <FormattedMessage id={loginTextId} />
            </Button>
        );
    } else if (isLAPayments && isAutopay && !hasCreditCard) {
        const classes = classNames(styles.primary, styles['action-button']);
        button = (
            <Button
                className={classes}
                locale={locale}
                onClick={registerForCatalog}
                primary
            >
                <FormattedMessage id="bidding_console.console.button.addPaymentMethod" />
            </Button>
        );
    } else if (pending) {
        const classes = classNames(styles.primary, styles['action-button'], styles.pending);
        button = (
            <Button
                className={classes}
                disabled
                locale={locale}
                primary
            >
                <FormattedMessage id="bidding_console.console.button.pending" />
            </Button>
        );
    } else if (declined) {
        const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted);
        const buttonText = <FormattedMessage id="bidding_console.console.button.declined" />;
        button = noLinks ? (
            <Button
                className={classes}
                disabled
                locale={locale}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
            >
                {buttonText}
            </Button>
        ) : (
            <Link
                className={styles['action-button']}
                locale={locale}
                to={getDeskSupportLink()}
            >
                {buttonText}
            </Link>
        );
    } else if (suspended || !accountInGoodStanding) {
        const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted);
        const buttonText = <FormattedMessage id="bidding_console.console.button.suspended" />;
        button = noLinks ? (
            <Button
                className={classes}
                disabled
                locale={locale}
                primary
                red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
            >
                {buttonText}
            </Button>
        ) : (
            <Link
                className={styles['action-button']}
                locale={locale}
                to={getDeskSupportLink()}
            >
                {buttonText}
            </Link>
        );
    } else if (limited || approved) {
        if (closed) {
            const classes = classNames(styles.tertiary, styles['action-button'], styles.submitted, {
                [styles.inactive]: !activeBidder,
                [styles.winning]: won,
            });
            button = (
                <Button
                    className={classes}
                    disabled
                    locale={locale}
                    primary
                    red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
                >
                    <FormattedMessage id="bidding_console.console.button.closed" />
                </Button>
            );
        } else if (myBid) {
            const classes = classNames(styles['action-button'], styles.winning);
            button = (
                <Button
                    className={classes}
                    locale={locale}
                    primary
                >
                    <FormattedMessage id="bidding_console.console.button.winning" />
                </Button>
            );
        } else {
            const classes = classNames(styles.tertiary, styles['action-button'], {
                [styles.submitted]: submitted,
            });

            const hoverClasses = classNames(styles['hover-wrapper'], {
                [styles.countdown]: showCountDown && !submitted,
            });

            button = (
                <div className={hoverClasses}>
                    {showToggleLockedSwitch && (
                        <div className={styles.switchWrapper}>
                            <Switch
                                activeBoxShadow={sliderActiveBoxShadow}
                                boxShadow={sliderBoxShadow}
                                checked={!biddingLocked}
                                checkedIcon={uncheckedIcon}
                                className={styles.switch}
                                handleDiameter={handleDiameter}
                                height={sliderHeight}
                                id="material-switch"
                                offColor={theme.colors.grey100}
                                onChange={toggleBiddingLocked}
                                onColor={theme.colors.teal}
                                onHandleColor={theme.colors.grey400}
                                uncheckedIcon={checkedIcon}
                                width={sliderWidth}
                            />
                        </div>
                    )}
                    <Button
                        className={classes}
                        disabled={submitted || !ready || wouldExceedBidLimit || biddingLocked || auctionPaused}
                        locale={locale}
                        onClick={placeBid}
                        primary
                        red={!isWhiteLabel || whiteLabelPrimaryColor === theme.colors.blue200}
                    >
                        {submitted ? (
                            <FormattedMessage
                                id="bidding_console.console.button.sent"
                                values={{ amount: formattedAsk }}
                            />
                        ) : bidderOutbid ? (
                            outbidMessage
                        ) : (
                            bidMessage
                        )}
                    </Button>
                </div>
            );
        }
    } else {
        const classes = classNames(styles.primary, styles['action-button'], {
            [styles.primaryVariant]: showItemPageVariant,
        });
        button = (
            <Button
                className={classes}
                locale={locale}
                onClick={registerForCatalog}
                primary
            >
                <FormattedMessage id="bidding_console.console.button.register_long" />
            </Button>
        );
    }

    return button;
};

export default BiddingConsoleButton;
